import { useState, useEffect } from 'react';

import { Autocomplete, Box, FormControl, Typography } from '@mui/material';

import { StyledTextField } from '../style';
import { CommandDefinition } from '../types';

interface CommandSelectorProps {
  commands: CommandDefinition[];
  selectedCommandId: string | null;
  onSelectCommand: (commandId: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  noRightBorder?: boolean; // Optional prop to control right border
  onInputEmpty?: (isEmpty: boolean) => void; // New prop to notify parent when input is empty
}

export const CommandSelector = ({
  commands,
  selectedCommandId,
  onSelectCommand,
  inputRef,
  noRightBorder,
  onInputEmpty,
}: CommandSelectorProps) => {
  const [inputValue, setInputValue] = useState('');

  // Report to parent component if the input is empty
  useEffect(() => {
    if (onInputEmpty) {
      onInputEmpty(inputValue === '');
    }
  }, [inputValue, onInputEmpty]);

  return (
    <Box mb={2} sx={{ height: '100%' }}>
      <FormControl
        fullWidth
        sx={{
          '& .MuiFormControl-fullWidth': { height: '100% !important' },
          height: '100% !important',
        }}
      >
        <Autocomplete
          style={{ flex: 1, height: '100%' }}
          value={commands.find((cmd) => cmd.id === selectedCommandId) || null}
          disablePortal
          autoComplete
          autoSelect
          autoHighlight
          options={commands}
          popupIcon={null} // Remove dropdown arrow icon
          noOptionsText="Not found"
          inputValue={inputValue}
          onInputChange={(_, value) => {
            setInputValue(value);
          }}
          onChange={(_, value) => {
            onSelectCommand(value?.id || '');
          }}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography fontWeight={500}>{option.label}</Typography>
                {option.description && (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <StyledTextField
              inputRef={inputRef}
              variant="filled"
              {...params}
              fullWidth
              label="Action"
              sx={{
                '& .MuiFilledInput-root': {
                  paddingRight: '32px !important',
                  ...(noRightBorder && {
                    borderRight: 'none',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    minHeight: '50px',
                    height: '100%',
                  }),
                },
                '& .MuiAutocomplete-input': {
                  fontSize: '14px',
                },
                '& .MuiAutocomplete-endAdornment': {
                  right: '4px',
                },
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );
};
